import { SkyDialog, SkyRadioGroup, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import {
  DEFAULT_SCHEMA_POLICY,
  SCHEMA_POLICY_LABELS,
  SCHEMA_POLICY_MODES,
  type SchemaPolicyMode,
} from "@shared/types/schemaChanges";
import { useSetSchemaPolicy } from "@/hooks/use-schema-changes";
import { isApiErrorCode } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";

/**
 * "Schema changes": what the load does when the source's columns change (product gaps row 7,
 * design section 4.5). Three modes, one help line each; read-only without `dataflows.write`.
 *
 * The choice saves on its own (`PUT /dataflows/:id/schema-policy`), apart from the connection's
 * Save: it is a setting of a saved connection, the default applies until then. Leaving "Review
 * every change" while new columns are held asks first (409 `SCHEMA_POLICY_CONFIRM`): confirming
 * approves them, so the next import adds them.
 */

export interface SchemaPolicySectionProps {
  dataflowId: string;
  mode: SchemaPolicyMode | undefined;
  canWrite: boolean;
}

/** The notice's tail when held columns were approved with the change; empty when none were. */
function approvedWaitingSentence(count: number): string {
  if (count > 0) {
    const columns = count === 1 ? "column was" : "columns were";
    const them = count === 1 ? "it" : "them";
    return ` ${count.toLocaleString()} held ${columns} approved. The next import adds ${them}.`;
  }
  return "";
}

export function SchemaPolicySection({ dataflowId, mode, canWrite }: SchemaPolicySectionProps) {
  const current = mode ?? DEFAULT_SCHEMA_POLICY;
  const setPolicy = useSetSchemaPolicy(dataflowId);
  const inline = useInlineError();
  const notice = useNotice();
  const [pending, setPending] = useState<SchemaPolicyMode | null>(null);
  const [confirm, setConfirm] = useState<{ mode: SchemaPolicyMode; detail: string } | null>(null);

  const apply = async (next: SchemaPolicyMode, applyWaiting: boolean): Promise<void> => {
    inline.clear();
    setPending(next);
    try {
      const result = await setPolicy.mutateAsync({ mode: next, applyWaiting });
      setConfirm(null);
      const approved = approvedWaitingSentence(result.approvedWaiting);
      notice.success(`Schema changes: ${SCHEMA_POLICY_LABELS[next].label}.${approved}`);
    } catch (error) {
      if (isApiErrorCode(error, "SCHEMA_POLICY_CONFIRM")) {
        setConfirm({ mode: next, detail: error.details[0]?.issue ?? error.message });
      } else {
        inline.show(error, "The schema change setting could not be saved.");
      }
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="schema-policy-section">
      <SkyRadioGroup
        testId="schema-policy"
        label="Schema changes"
        layout="vertical"
        value={pending ?? current}
        disabled={!canWrite || setPolicy.isPending}
        options={SCHEMA_POLICY_MODES.map((value) => ({
          value,
          label: SCHEMA_POLICY_LABELS[value].label,
          description: SCHEMA_POLICY_LABELS[value].help,
        }))}
        onChange={(next) => {
          const chosen = SCHEMA_POLICY_MODES.find((value) => value === next);
          if (chosen && chosen !== current) void apply(chosen, false);
        }}
      />
      {canWrite ? null : (
        <SkyText size={200} color="muted" testId="schema-policy-read-only">
          Changing this needs the dataflows.write permission.
        </SkyText>
      )}
      <InlineErrorBar
        error={inline.error}
        title={inline.error?.retryable ? undefined : "The schema change setting was not saved"}
        onDismiss={inline.clear}
        testId="schema-policy-error"
      />
      {confirm ? (
        <SkyDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirm(null);
          }}
          title="Approve the held columns?"
          size="small"
          testId="schema-policy-confirm"
          onBeforeClose={(reason) => reason !== "primaryAction"}
          primaryAction={{
            label: `Approve and switch to ${SCHEMA_POLICY_LABELS[confirm.mode].label.toLowerCase()}`,
            onClick: () => apply(confirm.mode, true),
            loading: setPolicy.isPending,
            disabled: setPolicy.isPending,
          }}
          secondaryAction={{ label: "Cancel", onClick: () => setConfirm(null) }}
        >
          <SkyText as="p" size={300} block>
            {confirm.detail} Switching approves them, and the next import adds them to the table.
          </SkyText>
        </SkyDialog>
      ) : null}
    </div>
  );
}
